import { useApolloClient } from '@apollo/client'
import {
  ShiftManagementBulkCopyUnassignedShiftsDocument,
  ShiftManagementCoverageSectionDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { apiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import useChangeView from 'modules/shift_management/hooks/useChangeView'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import { CopyScheduleResponse, Filters } from 'modules/shift_management/lib/types'
import type { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import UnassignedShiftProp from 'modules/shift_management/store/internal/models/UnassignedShift'
import type View from 'modules/shift_management/store/internal/models/View'

import { WizardData } from './utils'

interface Props {
  data: WizardData
  targetView: View
  onClose: () => void
}

// TODO: migrate to GraphQL mutation bulkCopyShiftmanagementSchedule
// when the backend returns shift and rest days in proper way to GraphQL (not entities)
const copySchedule = async (
  originalStartAt: string,
  targetStartAt: string,
  filters: Filters
): Promise<CopyScheduleResponse> => {
  const api = apiClient()

  // Send days_to_copy to 7, 28 or the month's days to copy based on view (week, 4weeks, month)
  return await api.post('/shift_management/schedules/bulk_copy', {
    original_start_at: originalStartAt,
    target_start_at: targetStartAt,
    ...filters,
  })
}

export const useCopyPeriodSchedule = ({ data, targetView, onClose }: Props) => {
  const [{ view }] = useSchedule()
  const { onChangePeriod } = useChangeView()
  const apolloClient = useApolloClient()
  const [{ employeeIds, teamIds, legalEntityIds, customFilters, locationIds }] = useFilters()
  const { running: submitting, performAction: handleScheduleSubmit } = useAsyncAction(
    async () => {
      app.setProgress({ message: i18n.t('shift_management.actions.copying_shifts') })
      const employees = employeeIds?.length ? employeeIds.map(Number) : []

      // TODO: Migrate to GraphQL mutations
      const result = await asyncAction(
        () =>
          copySchedule(data.startDateSchedule, data.destinationStartDate, {
            employee_ids: employees,
            logic_operator: LOGIC_OPERATOR.AND,
            custom_filters: customFilters,
            days_to_copy: data.daysSchedule,
            include_draft_shifts: true,
            team_ids: teamIds,
            legal_entity_ids: legalEntityIds,
            location_ids: locationIds,
          }),
        {
          successFlash: true,
          errorFlash: true,
        }
      )

      app.setProgress(null)

      if (isSuccessful(result)) {
        const { value: schedule } = result
        const copiedShiftsPayload = schedule.shifts ?? []
        const copiedRestDaysPayload = schedule.rest_days ?? []
        const impactedEmployees: string[] = uniq(copiedShiftsPayload.map((s) => s.employee_id))
        const hexCodes = copiedShiftsPayload.map((shift: ShiftPayload) => shift.color)
        const uniqHexCodes = uniq(hexCodes)
        const color = uniqHexCodes.map((code) => (code ? nameFromHexCode(code) : null))

        trackAddShiftSubmitEvent({
          view,
          employeeIds: impactedEmployees,
          shiftsCount: copiedShiftsPayload.length,
          restDaysCount: copiedRestDaysPayload.length,
          shiftType: 'copyPeriod',
          color,
          from: 'copy_period',
          withSearch: '',
          advancedOptions: false,
          extras: { days: data.daysSchedule },
        })

        onChangePeriod(targetView)
        // Refetch after view is changed so the query uses the new targetView dates
        await apolloClient.refetchQueries({ include: [ShiftManagementMainPageDocument] })

        onClose()
      }
    },
    {
      successMessage: i18n.t('shift_management.actions.copy_shifts_success'),
      errorFlash: false,
    }
  )

  return {
    submitting,
    handleScheduleSubmit,
  }
}

export const useCopyPeriodCoverage = ({ data, targetView, onClose }: Props) => {
  const [, dispatchSchedule] = useSchedule()
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const [bulkCopyUnassignedShifts, { loading }] = useMutation(
    ShiftManagementBulkCopyUnassignedShiftsDocument,
    {
      extractorFn: (x) => x.shiftManagementMutations.bulkCopyShiftmanagementUnassignedShift,
    }
  )

  const handleCoverageSubmit = async () => {
    const result = await bulkCopyUnassignedShifts({
      variables: {
        companyId: currentCompanyId,
        daysToCopy: data.daysCoverage,
        originalStartAt: data.startDateCoverage,
        targetStartAt: data.destinationStartDate,
      },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementCoverageSectionDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.copy_unassigned_shifts_success'),
      })

      const values = handled.data?.unassignedShifts || []
      const unassignedShiftsList = values.map((unassignedShift) =>
        UnassignedShiftProp.buildFromGraphQL(unassignedShift)
      )

      dispatchSchedule({ type: 'RESET_VIEW', view: targetView })
      dispatchUnassignedShifts({
        type: 'ADD_UNASSIGNED_SHIFTS',
        unassignedShifts: unassignedShiftsList,
      })

      onClose()
    }
  }

  return {
    submittingCoverage: loading,
    handleCoverageSubmit,
  }
}
